import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TableCell,
    TableRow,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    Typography,
    Chip,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Refresh as RefreshIcon,
    Replay as RefundIcon,
    ReceiptLong as RefundStatusIcon,
} from '@mui/icons-material';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { AppButton } from '../../../shared/components/AppButton';
import { Loader } from '../../../shared/components/Loader';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { getPayments, refundPayment, getRazorpayRefundStatus } from '../../../shared/services/paymentService';
import { Payment, PaymentStatus, PaymentMethod, RazorpayRefundStatus } from '../../../shared/types/payment';
import moment from 'moment';

export const Payments: React.FC = () => {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermission();
    const [payments, setPayments] = useState<Payment[]>([]);
    const [totalPayments, setTotalPayments] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | ''>('');
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');

    const [refundTarget, setRefundTarget] = useState<Payment | null>(null);
    const [refundReason, setRefundReason] = useState('');
    const [refunding, setRefunding] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const [statusTarget, setStatusTarget] = useState<Payment | null>(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusError, setStatusError] = useState('');
    const [refundStatus, setRefundStatus] = useState<RazorpayRefundStatus | null>(null);

    const canRefund = isSuperAdmin() || hasPermission('orders.update');

    useEffect(() => {
        loadPayments();
    }, [currentPage, pageSize, paymentStatus, paymentMethod, searchInput]);

    const loadPayments = async () => {
        try {
            setLoading(true);
            setError('');

            const params: any = {
                page: currentPage + 1,
                limit: pageSize,
                search: searchInput,
            };
            if (paymentStatus) params.paymentStatus = paymentStatus;
            if (paymentMethod) params.paymentMethod = paymentMethod;

            const response = await getPayments(params);

            if (response.success && response.data) {
                setPayments(response.data.items || []);
                setTotalPayments(response.data.pagination?.totalItems || 0);
            } else {
                setPayments([]);
                setTotalPayments(0);
            }
        } catch (err: any) {
            console.error('Error loading payments:', err);
            setError(err.response?.data?.message || 'Failed to load payments');
            setPayments([]);
            setTotalPayments(0);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmRefund = async () => {
        if (!refundTarget) return;
        try {
            setRefunding(true);
            await refundPayment(refundTarget.id, refundReason.trim() || undefined);
            setSnackbarMessage('Refund processed successfully');
            setSnackbarOpen(true);
            setRefundTarget(null);
            setRefundReason('');
            loadPayments();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to process refund');
        } finally {
            setRefunding(false);
        }
    };

    const handleViewRefundStatus = async (payment: Payment) => {
        setStatusTarget(payment);
        setRefundStatus(null);
        setStatusError('');
        try {
            setStatusLoading(true);
            const response = await getRazorpayRefundStatus(payment.id);
            if (response.success && response.data) {
                setRefundStatus(response.data);
            } else {
                setStatusError('Failed to fetch refund status');
            }
        } catch (err: any) {
            setStatusError(err.response?.data?.message || 'Failed to fetch refund status');
        } finally {
            setStatusLoading(false);
        }
    };

    const getStatusColor = (status: PaymentStatus) => {
        switch (status) {
            case 'PAID': return 'success';
            case 'PENDING': return 'warning';
            case 'FAILED': return 'error';
            case 'REFUND_INITIATED': return 'warning';
            case 'REFUNDED': return 'info';
            default: return 'default';
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(0);
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    const handlePaymentStatusChange = (event: SelectChangeEvent) => {
        setPaymentStatus(event.target.value as PaymentStatus | '');
        setCurrentPage(0);
    };

    const handlePaymentMethodChange = (event: SelectChangeEvent) => {
        setPaymentMethod(event.target.value as PaymentMethod | '');
        setCurrentPage(0);
    };

    const resetFilters = () => {
        setSearchInput('');
        setPaymentStatus('');
        setPaymentMethod('');
        setCurrentPage(0);
    };

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Payments
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <FormControl variant="outlined" size="small" className="min-w-[150px]">
                        <InputLabel>Payment Status</InputLabel>
                        <Select
                            value={paymentStatus}
                            onChange={handlePaymentStatusChange}
                            label="Payment Status"
                            disabled={loading}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="PENDING">Pending</MenuItem>
                            <MenuItem value="PAID">Paid</MenuItem>
                            <MenuItem value="FAILED">Failed</MenuItem>
                            <MenuItem value="REFUND_INITIATED">Refund Initiated</MenuItem>
                            <MenuItem value="REFUNDED">Refunded</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" className="min-w-[150px]">
                        <InputLabel>Method</InputLabel>
                        <Select
                            value={paymentMethod}
                            onChange={handlePaymentMethodChange}
                            label="Method"
                            disabled={loading}
                        >
                            <MenuItem value="">All Methods</MenuItem>
                            <MenuItem value="ONLINE">Online</MenuItem>
                            <MenuItem value="COD">COD</MenuItem>
                        </Select>
                    </FormControl>

                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search by ref, gateway/order ID"
                        className="w-full sm:w-64"
                        disabled={loading}
                    />

                    <Box display="flex" gap={2}>
                        <AppButton
                            variant="secondary"
                            startIcon={<RefreshIcon />}
                            onClick={loadPayments}
                            disabled={loading}
                        >
                            Refresh
                        </AppButton>
                        {(paymentStatus || paymentMethod || searchInput) && (
                            <AppButton
                                variant="secondary"
                                onClick={resetFilters}
                                disabled={loading}
                            >
                                Clear
                            </AppButton>
                        )}
                    </Box>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {loading && payments.length > 0 && <LinearProgress className="mb-4" />}

                    {loading && payments.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : payments.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">
                                {searchInput.trim() || paymentStatus || paymentMethod ? 'No payments match your filters.' : 'No payments found.'}
                            </Alert>
                        </Box>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">Payment</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Gateway</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Amount</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Status</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Date</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right">Actions</TableCell>
                                    </TableRow>
                                }
                                isEmpty={payments.length === 0}
                                emptyColSpan={6}
                            >
                                {payments.map((payment) => (
                                    <TableRow key={payment.id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                                {payment.paymentRef}
                                            </Typography>
                                            <Box mt={0.5}>
                                                <Chip label={payment.paymentMethod} size="small" variant="outlined" />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{payment.gateway}</Typography>
                                            {payment.gatewayOrderId && (
                                                <Typography variant="caption" color="textSecondary" display="block">
                                                    {payment.gatewayOrderId}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                ₹{payment.amount?.toLocaleString() || '0'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={payment.paymentStatus}
                                                color={getStatusColor(payment.paymentStatus)}
                                                size="small"
                                                className="font-bold"
                                            />
                                            {payment.paymentStatus === 'REFUNDED' && payment.refundedAt && (
                                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                                    {moment(payment.refundedAt).format('lll')}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">
                                                {moment(payment.createdAt).format('DD MMM YYYY, hh:mm A')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <div className="flex items-center justify-end gap-1">
                                                {payment.paymentStatus === 'PAID' && canRefund && (
                                                    <Tooltip title="Refund">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setRefundTarget(payment)}
                                                            sx={{ color: '#dc2626' }}
                                                        >
                                                            <RefundIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {(payment.paymentStatus === 'REFUND_INITIATED' || payment.paymentStatus === 'REFUNDED') && payment.gatewayRefundId && (
                                                    <Tooltip title="Check Live Refund Status">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewRefundStatus(payment)}
                                                            sx={{ color: '#7C3AED' }}
                                                        >
                                                            <RefundStatusIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="View Order">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/orders/view/${payment.orderId}`)}
                                                        sx={{ color: '#0EA5E9' }}
                                                    >
                                                        <ViewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>

                            <AppTablePagination
                                totalRows={totalPayments}
                                page={currentPage}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(0);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Refund Confirmation Dialog */}
            <Dialog open={!!refundTarget} onClose={() => setRefundTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Refund Payment</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This will refund ₹{refundTarget?.amount?.toLocaleString()} for payment{' '}
                        <strong>{refundTarget?.paymentRef}</strong> via Razorpay.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Reason (optional)"
                        multiline
                        rows={2}
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <AppButton variant="secondary" onClick={() => setRefundTarget(null)} disabled={refunding}>
                        Cancel
                    </AppButton>
                    <AppButton
                        variant="danger"
                        onClick={handleConfirmRefund}
                        disabled={refunding}
                        loading={refunding}
                    >
                        Refund
                    </AppButton>
                </DialogActions>
            </Dialog>

            {/* Live Refund Status Dialog */}
            <Dialog open={!!statusTarget} onClose={() => setStatusTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Live Refund Status</DialogTitle>
                <DialogContent dividers>
                    {statusLoading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <Loader />
                        </Box>
                    ) : statusError ? (
                        <Alert severity="error">{statusError}</Alert>
                    ) : refundStatus ? (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Box className="bg-blue-50 p-4 rounded-lg">
                                <Typography variant="caption" color="primary" fontWeight="bold" display="block" gutterBottom>
                                    REFUND STATUS
                                </Typography>
                                <Chip
                                    label={refundStatus.status}
                                    color={refundStatus.status === 'processed' ? 'success' : 'warning'}
                                    size="small"
                                    className="font-bold"
                                />
                            </Box>
                            <Box className="bg-gray-50 p-4 rounded-lg">
                                <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" gutterBottom>
                                    REFUND ID
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">{refundStatus.id}</Typography>
                            </Box>
                            <Box className="bg-gray-50 p-4 rounded-lg">
                                <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" gutterBottom>
                                    AMOUNT
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    ₹{refundStatus.amount?.toLocaleString()} {refundStatus.currency}
                                </Typography>
                            </Box>
                            {refundStatus.speedProcessed && (
                                <Box className="bg-gray-50 p-4 rounded-lg">
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" gutterBottom>
                                        REFUND SPEED
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold" className="capitalize">
                                        {refundStatus.speedProcessed}
                                    </Typography>
                                </Box>
                            )}
                            {refundStatus.createdAt && (
                                <Box className="bg-gray-50 p-4 rounded-lg">
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" gutterBottom>
                                        ISSUED ON
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {moment.unix(refundStatus.createdAt).format('DD MMM YYYY, hh:mm A')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <AppButton
                        variant="secondary"
                        onClick={() => statusTarget && handleViewRefundStatus(statusTarget)}
                        disabled={statusLoading}
                        loading={statusLoading}
                    >
                        Refresh
                    </AppButton>
                    <AppButton variant="secondary" onClick={() => setStatusTarget(null)}>
                        Close
                    </AppButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen || !!error}
                autoHideDuration={6000}
                onClose={() => { setSnackbarOpen(false); setError(''); }}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => { setSnackbarOpen(false); setError(''); }} severity={error ? 'error' : 'success'}>
                    {error || snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};
